import { toggleIdInList } from '@griply/utils';
import {
  useExternalConnectionCalendars,
  useUpdateExternalConnectionBlacklist,
} from 'features/external-connections';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import * as Styled from './calendar-connection-list.style';

export const LoadingCalendarConnectionList: React.FC = () => {
  const { t } = useTranslation();
  return <Styled.Button as="span">{t('general.labels.loading')}</Styled.Button>;
};

export type CalendarConnectionListProps = {
  connectionId: ID;
  blacklist?: ID[] | null;
};

export const CalendarConnectionList: React.FC<CalendarConnectionListProps> = ({
  connectionId,
  blacklist,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const calendars = useExternalConnectionCalendars(connectionId);
  const updateExternalConnectionBlacklist =
    useUpdateExternalConnectionBlacklist();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const activeCalendarsAmount = useMemo(
    () =>
      blacklist?.length
        ? calendars.filter(({ id }) => !blacklist.includes(id)).length
        : calendars.length,
    [calendars, blacklist],
  );

  return (
    <>
      <Styled.Button ref={buttonRef} onClick={openMenu}>
        {t('general.labels.calendar-count', {
          count: activeCalendarsAmount,
          total: calendars.length,
        })}
        <Styled.IconContainer>
          <Icon icon={ChevronLeft} />
        </Styled.IconContainer>
      </Styled.Button>

      {menuOpen && (
        <PopupMenu
          onOutside={closeMenu}
          referenceElement={buttonRef}
          position="bottom-end"
        >
          <PopupMenuList>
            {calendars.map((calendar) => {
              const onToggle = () =>
                updateExternalConnectionBlacklist(
                  connectionId,
                  toggleIdInList(blacklist, calendar.id),
                );
              return (
                <PopupMenuListItem key={calendar.id}>
                  <PopupMenuToggle
                    start={<Styled.CalendarColor $color={calendar.color} />}
                    label={calendar.name}
                    onChange={onToggle}
                    checked={!blacklist?.includes(calendar.id)}
                  />
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
